/**
 * API client — endpoints implemented by Codex (Workers + D1).
 * Until API is live, callers should fall back to local FakeFifo / local board.
 */

export type IdentityResponse = {
  playerToken: string;
  displayName: string;
  tag?: string;
};

export type ClearResponse = {
  clearTokenId: string;
};

export type MessageDto = {
  id: string;
  displayName: string;
  tag?: string;
  body: string;
  solo?: boolean;
};

function base(): string {
  return (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
}

export function apiConfigured(): boolean {
  return base().length > 0;
}

async function req<T>(
  path: string,
  init?: RequestInit & { token?: string },
): Promise<T> {
  const headers = new Headers(init?.headers);
  const method = (init?.method ?? "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD") {
    headers.set("content-type", "application/json");
  }
  if (init?.token) headers.set("authorization", `Bearer ${init.token}`);
  const res = await fetch(`${base()}${path}`, { ...init, headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${path}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export function postIdentity(displayName: string, tag?: string) {
  return req<IdentityResponse>("/identity", {
    method: "POST",
    body: JSON.stringify({ displayName, tag }),
  });
}

export function postClear(
  token: string,
  body: { runId: string; durationSec: number; helped: boolean },
) {
  return req<ClearResponse>("/runs/clear", {
    method: "POST",
    token,
    body: JSON.stringify(body),
  });
}

export function postMessage(
  token: string,
  body: { clearTokenId: string; body: string },
) {
  return req<MessageDto>("/messages", {
    method: "POST",
    token,
    body: JSON.stringify(body),
  });
}

export function getScoreboard(limit = 50) {
  return req<{ items: MessageDto[] }>(`/scoreboard?limit=${limit}`);
}

export function getHelpNext(token?: string, count = 1) {
  return req<{ items: MessageDto[] }>(`/help/next?count=${count}`, {
    method: "GET",
    token,
  });
}
